<template>
  <div
    class="autocomplete"
    :class="{
      'is-in-focus': isFocus
    }"
    :style="cssProps"
    @focus="isFocus = true"
    @blur="closeSuggestions()">
    <div v-if="title" class="autocomplete-header">
      {{ title }}
    </div>
    <input
      type="text"
      :class="computedClasses"
      :placeholder="placeholder"
      :max="maxlength"
      v-model="search"
      @focus="isFocus = true"
      @input="onInput($event)"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter="onEnter"/>
    <ul
      :id="`autocomplete-results${uniqueId}`"
      v-show="isFocus"
      class="autocomplete-results nonselectable-text"
      @blur="closeSuggestions">
      <li
        class="loading"
        v-if="isLoading">
        <div class="autocomplete-result autocomplete-result-disabled">
          {{ loadingResults }}
        </div>
      </li>
      <li
        v-else-if="noResults && !enableAdding"
        class="no-results">
        <div class="autocomplete-result autocomplete-result-disabled">
          {{ noResult }}
        </div>
      </li>
      <li
        v-else-if="noResults && enableAdding"
        class="autocomplete-result add-option"
        @click.prevent="add()"
        @keyup.enter="add()">
        <div class="autocomplete-result">
          + {{ search }}
        </div>
      </li>
      <li v-else>
        <div
          v-for="(result, i) in results"
          :key="i"
          @click.prevent="setResult(result);"
          @focus="isFocus = true"
          class="autocomplete-result"
          :class="{ 'is-active': i === arrowCounter }">
          {{ result.text }}
        </div>
        <div
          v-if="!isAsync && results.length < options.length"
          class="autocomplete-result autocomplete-result-disabled">
          {{ results.length }} {{ outOf }} {{ options.length }}
      </div>
      </li>
    </ul>
  </div>
</template>

<script>

export default {
  name: 'AutoComplete',
  props: {
    options: {
      required: true,
      type: Array,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Search..',
    },
    noResult: {
      type: String,
      required: false,
      default: 'No results..',
    },
    loadingResults: {
      type: String,
      required: false,
      default: 'Loading results..',
    },
    outOf: {
      type: String,
      required: false,
      default: 'out of',
    },
    maxlength: {
      type: Number,
      required: false,
      default: null,
    },
    enableAdding: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
    isAsync: {
      type: Boolean,
      required: false,
      default: false,
    },
    uniqueId: {
      type: String,
      required: false,
      default: '',
    },
    limit: {
      type: Number,
      required: false,
      default: 10,
    },
    accentColor: {
      type: String,
      required: false,
      default: '#CCCCCC',
    },
    textColor: {
      type: String,
      required: false,
      default: 'black',
    },
    resultPosition: {
      type: String,
      required: false,
      default: 'absolute',
    },
  },
  data() {
    return {
      isFocus: false,
      results: [],
      search: '',
      isLoading: false,
      arrowCounter: -1,
      addNew: false,
    };
  },
  computed: {
    noResults() {
      return !this.results.length;
    },
    cssProps() {
      return {
        '--autocomplete-accent-color': this.accentColor,
        '--autocomplete-text-color': this.textColor,
        '--autocomplete-result-position': this.resultPosition,
      }
    },
    computedClasses() {
      return {
        '--focus': this.isFocus,
      };
    },
  },
  mounted() {
    this.search = this.value;
    this.filterResults();
  },
  /*
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },*/
  watch: {
    value(newVal, oldVal) {
      // console.log(newVal);
      if (newVal.length !== oldVal.length) {
        this.search = this.value;
        // if (!this.options.find(el => el.text === newVal).length) {
        //   this.options.push({ value: newVal, text: newVal });
        // }
      }
    },
    options(newVal, oldVal) {
      if (this.isAsync || newVal.length !== oldVal.length) {
        this.results = newVal;
        // this.isFocus = true;
        this.isLoading = false;
        // this.filterResults();
      }
    },
  },
  methods: {
    onInput(data) {
      this.search = data.target.value;
      this.isFocus = true;
      this.arrowCounter = -1;

      // Let's warn the parent that a change was made
      this.$emit('input', this.search);
      if (this.search === '') {
        this.$emit('selected', {});
      }

      // Is the data given by an outside ajax request?
      if (this.isAsync) {
        this.isLoading = true;
      } else {
        // Data is sync, we can search our flat array
        this.filterResults();
      }
    },
    filterResults() {
      this.results = [];
      for (let i = 0; i < this.options.length; i += 1) {
        if (this.options[i].text
          && this.options[i].text.toLowerCase().includes((this.search || '').toLowerCase())) {
          if (this.results.length < this.limit) {
            this.results.push(this.options[i]);
          }
        }
      }
    },
    setResult(result) {
      this.search = result.text;
      this.$emit('selected', result);
      this.closeSuggestions();
      this.filterResults();
    },
    onArrowDown() {
      if (this.arrowCounter < this.results.length - 1) {
        this.arrowCounter = this.arrowCounter + 1;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter -1;
      }
    },
    onEnter() {
      if (this.results.length && this.arrowCounter !== -1) {
        const result = this.results[this.arrowCounter];
        this.search = result.text;
        this.$emit('selected', result);
        this.closeSuggestions();
      }
    },
    add() {
      this.$emit('addOption', this.search);
      this.closeSuggestions();
    },
    closeSuggestions() {
      console.log('clicked somewhere else');
      this.isFocus = false;
      this.arrowCounter = -1;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "./AutoComplete";
</style>
